// src/components/Sidebar.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Users, PenSquare, Video, MessageSquare, ChevronDown, ChevronUp, Sun, Moon } from 'lucide-react';
import { useThemeContext } from './ThemeWrapper';

// --- Sidebar Item Component ---
interface SidebarItemProps {
  icon: React.ComponentType<any>;
  label: string;
  active: boolean;
  onClick: () => void;
  isDarkTheme: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon: Icon,
  label,
  active,
  onClick,
  isDarkTheme,
}) => {
  return (
    <motion.div
      className={`flex items-center space-x-3 py-3 px-4 rounded-xl cursor-pointer transition-colors duration-200
        ${
          active
            ? `${isDarkTheme ? 'bg-emerald-100 text-emerald-400 font-semibold shadow-md' : 'bg-emerald-100 text-emerald-600 font-semibold shadow-md'}`
            : `${isDarkTheme ? 'text-gray-300 hover:bg-zinc-800 hover:text-white' : 'text-gray-700 hover:bg-gray-200'}`
        }`}
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Icon size={20} />
      <span>{label}</span>
    </motion.div>
  );
};

// --- Pretty Theme Switch ---
const ThemeSwitch: React.FC<{ isDarkTheme: boolean; toggleTheme: () => void }> = ({ isDarkTheme, toggleTheme }) => {
  return (
    <motion.button
      onClick={toggleTheme}
      whileTap={{ scale: 0.9 }}
      className={`relative w-14 h-7 flex items-center rounded-full p-1 transition-colors duration-300 
        ${isDarkTheme ? 'bg-gray-700' : 'bg-yellow-300'}`}
    >
      <motion.div
        className="absolute left-1"
        animate={{ x: isDarkTheme ? 28 : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        {isDarkTheme ? (
          <Moon className="text-white w-5 h-5" />
        ) : (
          <Sun className="text-yellow-700 w-5 h-5" />
        )}
      </motion.div>
    </motion.button>
  );
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkTheme, toggleTheme } = useThemeContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const sidebarItems = [
    { icon: Home, label: 'Learn', path: '/learn' },
    { icon: Users, label: 'Characters', path: '/hiragana' },
    { icon: PenSquare, label: 'Practice board', path: '/whiteboard' },
    { icon: Video, label: 'Videos', path: '/videos' },
    { icon: MessageSquare, label: 'Conversation', path: '/conversation' },
  ];

  const currentPath = location.pathname;

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col w-64 p-5 border-r
          ${isDarkTheme ? 'bg-black border-gray-700' : 'bg-white border-gray-300'}`}
      >
        <div className="flex items-center space-x-2 mb-10">
          <img
            src="https://placehold.co/40x40/f3f4f6/334155?text=N"
            alt="Nekyo"
            className="rounded-full"
          />
          <h1
            className={`${isDarkTheme ? 'text-emerald-400' : 'text-emerald-600'} text-2xl font-bold`}
          >
            Nekyo
          </h1>
        </div>

        <nav className="space-y-2">
          {sidebarItems.map((item) => (
            <SidebarItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              active={currentPath === item.path}
              onClick={() => navigate(item.path)}
              isDarkTheme={isDarkTheme}
            />
          ))}

          {/* Theme Switch */}
          <div className="flex justify-start p-4">
            <ThemeSwitch isDarkTheme={isDarkTheme} toggleTheme={toggleTheme} />
          </div>
        </nav>
      </aside>

      {/* Bottom Mobile Navigation */}
      <nav
        className={`fixed bottom-0 left-0 w-full md:hidden flex justify-around items-center h-16 z-50 border-t
          ${isDarkTheme ? 'bg-black border-gray-700' : 'bg-white border-gray-300'}`}
      >
        {sidebarItems.map((item) => (
          <motion.button
            key={item.path}
            className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors duration-200
              ${
                currentPath === item.path
                  ? `${isDarkTheme ? 'text-emerald-400' : 'text-emerald-600'}`
                  : `${isDarkTheme ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-black'}`
              }`}
            onClick={() => navigate(item.path)}
            whileTap={{ scale: 0.9 }}
          >
            <item.icon size={22} />
            <span className="text-xs mt-1">{item.label}</span>
          </motion.button>
        ))}

        {/* Dropdown Toggle */}
        <div className="flex items-center justify-center p-2">
          <motion.button
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            whileTap={{ scale: 0.9 }}
            className={`p-2 rounded-full transition-colors duration-200 ${
              isDarkTheme
                ? 'text-gray-300 hover:text-white'
                : 'text-gray-700 hover:text-black'
            }`}
          >
            {isDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </motion.button>
        </div>
      </nav>

      {/* Expanded Dropdown Panel - Only Mobile/Tablet */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`fixed bottom-16 left-0 w-full z-40 border-t shadow-lg md:hidden
              ${isDarkTheme ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-300'}`}
          >
            <div className="flex flex-col items-center p-4 space-y-3">
              {/* Theme Switch */}
              <ThemeSwitch isDarkTheme={isDarkTheme} toggleTheme={toggleTheme} />

              {/* Login / Signup Buttons */}
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="w-11/12 py-2 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700"
              >
                Login
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="w-11/12 py-2 rounded-xl font-semibold text-white bg-emerald-600 hover:bg-emerald-700"
              >
                Sign Up
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
